import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import AppContext from "../../context/AppContext";
import { isRTL } from "../../utils/rtl";
import translations from "./Translations";

// A month + year picker in the app's own idiom rather than react-datepicker's
// month mode, whose stylesheet renders a single narrow column of months here and
// fights the Tailwind base layer. Values are plain Dates pinned to the first of
// the month, so callers can format them however the backend expects.
const MONTH_LABELS = Array.from({ length: 12 }, (_, index) =>
  new Date(2000, index, 1).toLocaleDateString("en-US", { month: "short" })
);

const PANEL_WIDTH = 268;
const PANEL_HEIGHT = 300;
const GAP = 6;

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const MonthYearPicker = ({
  value = null,
  onChange,
  minDate = null,
  placeholder = "",
  disabled = false,
  // Same escape hatch CommonReactSelector exposes, so a screen that tints its
  // selects can tint the picker to match instead of the picker hardcoding one
  // screen's palette.
  background,
  dataTestId = "month-year-picker",
}) => {
  const { theme, langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const isDark = theme === "dark";
  const isHighContrast = theme === "high-contrast";

  const [isOpen, setIsOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const [viewYear, setViewYear] = useState(
    () => (value || minDate || new Date()).getFullYear()
  );
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const minMonth = minDate ? startOfMonth(minDate) : null;
  const canGoToPreviousYear = !minMonth || viewYear > minMonth.getFullYear();

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Opens downward unless the viewport can't hold it, in which case it flips
    // above the trigger rather than spilling off-screen.
    const fitsBelow = window.innerHeight - rect.bottom >= PANEL_HEIGHT + GAP;
    const top = fitsBelow ? rect.bottom + GAP : rect.top - PANEL_HEIGHT - GAP;

    // Anchored to the trigger's leading edge, then clamped so it can never sit
    // outside the viewport on narrow screens.
    const preferredLeft = isRtl ? rect.right - PANEL_WIDTH : rect.left;
    const left = Math.min(
      Math.max(preferredLeft, GAP),
      Math.max(window.innerWidth - PANEL_WIDTH - GAP, GAP)
    );

    setPanelStyle({
      position: "fixed",
      top: Math.max(top, GAP),
      left,
      width: PANEL_WIDTH,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    updatePosition();
    const onReflow = () => updatePosition();
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isRtl]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !panelRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const openPanel = () => {
    // Always reopen on the selected month's year, not wherever the user last
    // browsed to.
    setViewYear((value || minDate || new Date()).getFullYear());
    setIsOpen(true);
  };

  const handleSelectMonth = (monthIndex) => {
    onChange?.(new Date(viewYear, monthIndex, 1));
    setIsOpen(false);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.(null);
    setIsOpen(false);
  };

  const triggerLabel = value
    ? `${MONTH_LABELS[value.getMonth()]} ${value.getFullYear()}`
    : placeholder;

  /* The trigger mirrors CommonReactSelector's `control` styles state for state
     -- resting border, hover border, focus halo and the disabled palette --
     because the two sit side by side in the same toolbars and any divergence
     reads as one of them being broken. Geometry (42px / 10px / 15px) already
     matched; these are the interactive states that did not. */
  const triggerToneClass = isDark
    ? "border-slate-600 text-slate-100"
    : isHighContrast
      ? "high-contrast border-2"
      : "border-[#cbd5e1] text-gray-900";

  const triggerStateClass = disabled
    ? isDark
      ? "border-slate-700"
      : "border-[#e2e8f0]"
    : isOpen
      ? isDark
        ? "border-[#818cf8] shadow-[0_0_0_3px_rgba(129,140,248,0.25)]"
        : "border-[#236DB4] shadow-[0_0_0_3px_rgba(35,109,180,0.15)]"
      : `shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${
          isDark ? "" : isHighContrast ? "" : "hover:border-[#94a3b8]"
        }`;

  /* High contrast paints itself through its own class, so leave its background
     alone rather than overriding it inline. */
  const triggerBackground = isHighContrast
    ? undefined
    : disabled
      ? isDark
        ? "#1e293b"
        : "#f8fafc"
      : background || (isDark ? "#1e293b" : "#ffffff");

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (isOpen ? setIsOpen(false) : openPanel())}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid={dataTestId}
        dir={isRtl ? "rtl" : "ltr"}
        style={triggerBackground ? { background: triggerBackground } : undefined}
        className={`flex h-[42px] w-full items-center gap-2 rounded-[10px] border px-3 text-[15px] transition-all duration-200 disabled:cursor-not-allowed ${triggerToneClass} ${triggerStateClass}`}
      >
        <CalendarDays size={15} className="shrink-0 opacity-60" />
        <span
          className={`min-w-0 flex-1 truncate text-start ${
            value ? "font-medium" : isDark ? "text-slate-500" : "text-slate-400"
          }`}
        >
          {triggerLabel}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            aria-label={translations[langMode].clear}
            data-testid={`${dataTestId}-clear`}
            className={`shrink-0 rounded p-0.5 transition-colors ${
              isDark ? "hover:bg-slate-700" : "hover:bg-slate-200"
            }`}
          >
            <X size={14} className="opacity-60" />
          </span>
        )}
        {/* Matches CommonReactSelector's DropdownIndicator -- 16px chevron in
            #64748b that flips when open. Without it the control read as a plain
            button rather than something that opens a panel. */}
        <ChevronDown
          size={16}
          strokeWidth={2.5}
          className={`shrink-0 text-[#64748b] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            style={panelStyle}
            dir={isRtl ? "rtl" : "ltr"}
            data-testid={`${dataTestId}-panel`}
            className={`z-[100000] rounded-[10px] border p-3 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.12),0_4px_6px_-2px_rgba(0,0,0,0.04)] ${
              isDark
                ? "border-slate-600 bg-slate-800"
                : isHighContrast
                  ? "high-contrast border-2"
                  : "border-[#cbd5e1] bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewYear((year) => year - 1)}
                disabled={!canGoToPreviousYear}
                aria-label={translations[langMode].previousyear}
                data-testid={`${dataTestId}-prev-year`}
                className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
                  isDark
                    ? "text-slate-300 enabled:hover:bg-slate-700"
                    : "text-slate-600 enabled:hover:bg-slate-100"
                }`}
              >
                {isRtl ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>

              <span
                className={`text-sm font-bold tabular-nums ${
                  isDark ? "text-slate-100" : isHighContrast ? "" : "text-slate-800"
                }`}
                data-testid={`${dataTestId}-year`}
              >
                {viewYear}
              </span>

              <button
                type="button"
                onClick={() => setViewYear((year) => year + 1)}
                aria-label={translations[langMode].nextyear}
                data-testid={`${dataTestId}-next-year`}
                className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
                  isDark
                    ? "text-slate-300 hover:bg-slate-700"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {isRtl ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-1.5">
              {MONTH_LABELS.map((label, monthIndex) => {
                const isBeforeMin =
                  minMonth && new Date(viewYear, monthIndex, 1) < minMonth;
                const isSelected =
                  value &&
                  value.getFullYear() === viewYear &&
                  value.getMonth() === monthIndex;

                let stateClasses;
                if (isSelected) {
                  stateClasses = isHighContrast
                    ? "high-contrast border-2 font-semibold"
                    : "bg-[#236DB4] font-semibold text-white shadow-sm";
                } else if (isBeforeMin) {
                  stateClasses = isDark ? "text-slate-600" : "text-slate-300";
                } else {
                  stateClasses = isDark
                    ? "text-slate-200 hover:bg-slate-700"
                    : "text-slate-700 hover:bg-[rgba(35,109,180,0.08)] hover:text-[#236DB4]";
                }

                return (
                  <button
                    key={label}
                    type="button"
                    disabled={isBeforeMin}
                    onClick={() => handleSelectMonth(monthIndex)}
                    data-testid={`${dataTestId}-month-${monthIndex}`}
                    className={`h-9 rounded-lg text-sm transition-colors disabled:cursor-not-allowed ${stateClasses}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MonthYearPicker;
